import type { NotificationType } from "../shared/types/notificationSettingsTypes";
import type { CreateNotificationSettingsResponse } from "../shared/payloads/notificationSettingsPayloads";

import { NotificationManager } from "../services/api/NotificationManager";

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { getDefaultSettings } from "../utils/helpers/getDefaultSettings";
import { convertStringToLanguage } from "../generic-utils";
import { showFlashMessageWithTimeout } from "../services/actions/flashMessage-actions";

interface UseNotificationManagerReturn {
  createNotificationPrimitive: (
    type: NotificationType,
    options?: { useDefaults?: boolean; updateState?: boolean },
  ) => Promise<
    | {
        id: string;
        type: NotificationType;
      }
    | undefined
  >;
}

export const useNotificationManager = (): UseNotificationManagerReturn => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const language = convertStringToLanguage(i18n.language);
  const defaultSettings = getDefaultSettings(language, t);
  const _notificationManager = NotificationManager.getManager();

  const createNotificationPrimitive = async (
    type: NotificationType,
    options?: { useDefaults?: boolean; updateState?: boolean },
  ): Promise<
    | {
        id: string;
        type: NotificationType;
      }
    | undefined
  > => {
    if (!id) return;
    let defaultSet;
    if (options?.useDefaults) {
      switch (type) {
        case "lastOrders":
          defaultSet = defaultSettings.lastOrders;
          break;
        case "lastRegistrations":
          defaultSet = defaultSettings.lastRegistrations;
          break;
        case "actualVisitors":
          defaultSet = defaultSettings.actualVisitors;
          break;
        case "visitorSummary":
          defaultSet = defaultSettings.visitorSummary;
          break;
        case "heureka":
          defaultSet = defaultSettings.heureka;
          break;
        case "zboziCompanyRating":
          defaultSet = defaultSettings.zboziCompanyRating;
          break;
        case "spolehlivaRecenzeShopRating":
          defaultSet = defaultSettings.spolehlivaRecenzeShopRating;
          break;
        case "individual":
          defaultSet = defaultSettings.individual;
          break;
        case "shoptetLastOrders":
          defaultSet = defaultSettings.shoptetLastOrders;
          break;
        case "shopifyLastOrders":
          defaultSet = defaultSettings.shopifyLastOrders;
          break;
        case "eshopRychleLastOrders":
          defaultSet = defaultSettings.eshopRychleLastOrders;
          break;
        case "shoptetCouponCode":
          defaultSet = defaultSettings.shoptetCouponCode;
          break;
        case "googleReviews":
          defaultSet = defaultSettings.googleReviews;
          break;
        case "trustpilotReviews":
          defaultSet = defaultSettings.trustpilotReviews;
          break;
        default:
          throw new Error(`Unexpected type of notification: ${type}`);
      }
    }
    try {
      const result: CreateNotificationSettingsResponse =
        await _notificationManager.createNotificationSettingsAsync(
          id,
          type,
          defaultSet,
        );
      if (result.id) {
        return {
          id: result.id,
          type,
        };
      } else {
        throw new Error(t("popups.cannotCreateNotification") ?? "");
      }
    } catch (e: any) {
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  return {
    createNotificationPrimitive,
  };
};
